import {
  Container,
  Grid,
  Card,
  Typography,
  IconButton,
  TextField,
  Autocomplete,
  Paper,
  Button,
  Avatar,
  Box,
  Modal,
  CardContent,
  Stack,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import CircleIcon from '@mui/icons-material/Circle';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import CloudCircleIcon from '@mui/icons-material/CloudCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { notification } from 'antd';
const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.txt', '.csv', '.docx', '.xlsx'];
const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'text/plain',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
import { ArrowCircleDownOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import CardMedia from '@mui/material/CardMedia';
import { pdfjs } from 'react-pdf';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import logger from '../../utils/logger';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import { formatEpochToDateString } from '../../components/helper';
import DialogUpload from '../../components/dialog/alert-detail/upload';
import DialogPreview from '../../components/dialog/alert-detail/preview';
import Nopreview from '../../style/no-preview.png';
import ImageNull from '../../style/imagenull.png';
import { getRole, getToken, getAclMenu } from '../../utils/common';
import { getTypographyColor, getStatusText, getCardBackground, getHistoryStatusText } from './contants';
import {
  getInvalidObject,
  getValidObject,
  getTypographyColor as getTypographyColorOld,
  getStatusText as getStatusTextOld,
  getInputStyles,
} from './generateObjectHelpers';

// Try jsdelivr with legacy build path for version 5.x
// Legacy build should be more compatible with react-pdf
const pdfjsVersion = pdfjs.version || '5.4.296';
const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/legacy/build/pdf.worker.min.mjs`;
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

// Helper functions moved to outer scope for better performance
function setNullRecursive(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return null;
  }

  if (Array.isArray(obj)) {
    return obj.map(setNullRecursive);
  }

  const result = {};
  for (const key of Object.keys(obj)) {
    result[key] = setNullRecursive(obj[key]);
  }

  return result;
}

function transformObjectStructure(target, template) {
  for (const key of Object.keys(template)) {
    const tVal = template[key];
    const isObj = tVal !== null && typeof tVal === 'object';
    const hasKey = Object.hasOwn(target, key);
    const cur = hasKey ? target[key] : null;

    if (isObj) {
      if (cur === null || typeof cur !== 'object') target[key] = {};
      transformObjectStructure(target[key], tVal);
      continue;
    }

    if (cur === null || cur === undefined) target[key] = '';
  }
  return target;
}

function reorderObject(obj1, obj2) {
  const isObj = (v) => v !== null && typeof v === 'object';
  const hasOwn = (o, k) => Object.hasOwn(o, k);

  function reorder(a, b) {
    // Primitives (strings, numbers, etc.) just mirror b
    if (!isObj(a)) return b;

    const out = {};
    const bSafe = isObj(b) ? b : {};
    for (const key of Object.keys(a)) {
      out[key] = hasOwn(bSafe, key) ? reorder(a[key], bSafe[key]) : null;
    }
    return out;
  }

  const result = {};
  const obj2Safe = isObj(obj2) ? obj2 : {};
  for (const key of Object.keys(obj1)) {
    result[key] = hasOwn(obj2Safe, key) ? reorder(obj1[key], obj2Safe[key]) : null;
  }
  return result;
}

const parseToDate = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const timestamp = value < 1e12 ? value * 1000 : value;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const numericValue = Number(trimmed);
    if (!Number.isNaN(numericValue) && trimmed === `${numericValue}`) {
      const timestamp = numericValue < 1e12 ? numericValue * 1000 : numericValue;
      const date = new Date(timestamp);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateInJakarta = (value, options) => {
  const date = parseToDate(value);
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('id-ID', {
    timeZone: 'Asia/Jakarta',
    ...options,
  }).format(date);
};

const formatToJakartaDate = (value) =>
  formatDateInJakarta(value, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

const formatToJakartaDateTime = (value) => {
  const formatted = formatDateInJakarta(value, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  return formatted ? `${formatted} WIB` : '';
};

export default function DetailAlert() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id');
  const [acl] = useState(getAclMenu('action'));
  const [datadecoded, setDatadecoded] = React.useState({});
  const [data, setData] = React.useState('');
  const [orderlist, setOrderlist] = React.useState([]);
  const [date, setDate] = React.useState(null);
  const [action, setAction] = React.useState([]);
  const [files, setFiles] = useState([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [selectedIndexafter, setSelectedIndexafter] = React.useState(0);
  const [history, setHistory] = React.useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imagePreviews2, setImagePreviews2] = useState([]);
  const [loading, setloading] = useState({ loading1: false, loading2: false });
  const [datachanges, setDatachanges] = useState([]);
  const [newstatusprocess, setNewstatusprocess] = useState({
    status: '',
    closed_status: '',
    note: '',
  });
  const [image] = React.useState({
    nameFileAfter: [],
    nameFileBefore: [],
    byteImageAfter: [],
    ByteImageBefore: [],
  });
  const [dialogUpload, setDialogUpload] = React.useState({
    open: false,
    handleClose: () => {
      setDialogUpload((prevState) => ({ ...prevState, open: !prevState.open }));
    },
  });
  const [dialogUploadPreview, setDialogUploadPreview] = React.useState({
    open: false,
    base64String: '',
    type: '',
    path: '',
    filename: '',
  });

  const handleClosePreview = () => {
    setDialogUploadPreview((prevState) => ({ ...prevState, open: false }));
  };
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchInfo = (url, decodedId) =>
    fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((d) => {
        if (d.code === 401) {
          setData('');
        } else {
          setData({
            ...d,
            status: JSON.parse(decodedId)?.status,
          });
          fetchInfo33(JSON.parse(decodedId)?.recordId);

          if (JSON.parse(decodedId).status === 'open') {
            fetchInfo5(
              `${globalThis.REACT_APP_API_PURE}action/list?assign=${JSON.parse(decodedId)?.dataSet}`,
              setAction
            );
          } else {
            fetchInfo5(`${globalThis.REACT_APP_API_PURE}alert/action?id=${JSON.parse(decodedId)?.id}`, setAction);
          }

          if (d?.at(-1)?.histories?.length > 0) {
            setDate(d?.at(-1)?.histories[d?.at(-1)?.histories?.length - 1]?.epoch);

            setOrderlist(d?.at(-1).histories);

            let selectedIndexafter = d?.at(-1).histories.length;
            const hashAfter = [];

            if (selectedIndexafter > 1) {
              hashAfter.push(d?.at(-1).histories[selectedIndexafter - 1]);
            } else {
              hashAfter.push(d?.at(-1).histories[1]);
            }
            selectedIndexafter = d?.at(-1).histories?.findIndex((option) => option?.epoch === hashAfter[0]?.epoch) - 1;

            fetchInfo4(
              `${globalThis.REACT_APP_API_PURE}record/compare/${JSON.parse(decodedId)?.recordId}/${hashAfter[0]?.id}`,
              setData,
              { isChange: true },
              0,
              selectedIndexafter < 1 ? 0 : selectedIndexafter
            );
          } else {
            setDate(d?.at(0)?.histories[d?.at(0)?.histories?.length - 0]?.epoch);

            setOrderlist(d?.at(0).histories);
            let selectedIndexafter = d?.at(0).histories.length;
            const hashAfter = [];

            if (selectedIndexafter > 1) {
              hashAfter.push(d?.at(0).histories[selectedIndexafter - 0]);
            } else {
              hashAfter.push(d?.at(0).histories[0]);
            }
            selectedIndexafter = d?.at(-1).histories?.findIndex((option) => option?.epoch === hashAfter[0]?.epoch) - 1;

            fetchInfo4(
              `${globalThis.REACT_APP_API_PURE}record/compare/${JSON.parse(decodedId)?.recordId}/${hashAfter[0]?.id}`,
              setData,
              { isChange: true },
              0,
              selectedIndexafter < 1 ? 0 : selectedIndexafter
            );
          }
        }
      });

  const findPath = (obj, target) => {
    for (const key of Object.keys(obj)) {
      if (key === target) {
        return [key];
      }
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        const path = findPath(obj[key], target);
        if (path.length > 0) {
          return [key, ...path];
        }
      }
    }
    return [];
  };

  const replaceValuesWithEmpty = (obj) => {
    for (const key of Object.keys(obj)) {
      if (typeof obj[key] === 'object') {
        replaceValuesWithEmpty(obj[key]);
      } else {
        obj[key] = '';
      }
    }
  };

  const fetchInfo4 = useCallback(
    async (url, setdata, { isChange = true }, selectedIndex, selectedIndexafter) => {
      try {
        fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        })
          .then((res) => res.json())
          .then((d) => {
            if (isChange) setImagePreviews([]);
            setImagePreviews2([]);

            const databeforeupdate = d?.before || [];

            const dataafterupdate = d?.after || [];
            let transformeddataafter = {};

            if (dataafterupdate.length === 0) {
              transformeddataafter = setNullRecursive(databeforeupdate);
            } else {
              transformeddataafter = transformChangesRemove(
                [...(d.updates || []), ...(d.invalids || [])],
                dataafterupdate
              );
              transformObjectStructure(databeforeupdate, transformeddataafter);
            }
            transformeddataafter = reorderObject(databeforeupdate, transformeddataafter);

            if (isChange) {
              setSelectedIndex(selectedIndex);
              setSelectedIndexafter(selectedIndexafter);
              setdata([
                {
                  history: {
                    after: transformeddataafter,
                    before: databeforeupdate,
                    invalids: d?.invalids,
                    updates: d?.updates,
                  },
                  status: datadecoded.status,
                },
              ]);

              fetchImage(databeforeupdate, transformeddataafter);
            } else {
              setdata([
                {
                  history: {
                    after: d?.after,
                    before: data[0]?.history?.before,
                    invalids: d?.invalids,
                    updates: d?.updates,
                  },
                  status: datadecoded.status,
                },
              ]);
              fetchImage([], d?.after);
            }
          });
      } catch (error) {
        logger.error('Error in fetchInfo4:', error);
      }
    },
    [data, setData]
  );

  const fetchInfo5 = (url, setdata) =>
    fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((d) => {
        setdata(d);
      });

  const fetchInfo7 = (url, setdata) =>
    fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((d) => {
        setdata(d?.data || []);
      });

  const fetchInfo6 = async (status, keterangan) => {
    const recreatefiles =
      files?.map((item) => ({
        name: item.name || item.nama_file,
        size: item.size || 0,
        type: item.type || '',
        contents: item.contents || item.file,
      })) || [];

    try {
      const res = await fetch(`${globalThis.REACT_APP_API_PURE}alert/edit`, {
        method: 'POST',
        body: JSON.stringify({
          id: datadecoded.id,
          status,
          action: action?.data,
          note: keterangan,
          files: recreatefiles,
        }),
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      });
      const d = await res.json();
      if (d.response_code === 200) {
        notification.success({
          message: 'Success',
          description: t('SuccessAlert.1'),
          placement: 'top',
          duration: 5,
        });
      } else {
        notification.error({
          message: 'Error',
          description: t('SuccessAlert.2'),
          placement: 'top',
          duration: 5,
        });
      }
      navigate('/dashboard/alert');
    } catch (error) {
      logger.error('Error in fetchInfo6:', error);
      notification.error({
        message: 'Error',
        description: t('SuccessAlert.2'),
        placement: 'top',
        duration: 0,
      });
      throw error;
    }
  };

  const fetchapprovechecker = async (status, keterangan, note = '') => {
    const recreatefiles =
      files?.map((item) => ({
        name: item.name || item.nama_file,
        size: item.size || 0,
        type: item.type || '',
        contents: item.contents || item.file,
      })) || [];

    try {
      const res = await fetch(`${globalThis.REACT_APP_API_PURE}alert/edit/approvereject`, {
        method: 'POST',
        body: JSON.stringify({
          id: datadecoded.id,
          status,
          action: action?.data,
          note: keterangan,
          comment: note,
          files: recreatefiles,
        }),
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      });
      const d = await res.json();
      if (d.response_code === 200) {
        notification.success({
          message: 'Success',
          description: t('SuccessAlert.1'),
          placement: 'top',
          duration: 5,
        });
      } else {
        notification.error({
          message: 'Error',
          description: t('SuccessAlert.2'),
          placement: 'top',
          duration: 5,
        });
      }
      navigate('/dashboard/alert');
    } catch (error) {
      logger.error('Error in fetchapprovechecker:', error);
      notification.error({
        message: 'Error',
        description: t('SuccessAlert.2'),
        placement: 'top',
        duration: 0,
      });
      throw error;
    }
  };

  const fetchImage = async (before, after) => {
    // turn both spinners on
    setloading((s) => ({ ...s, loading1: true, loading2: true }));

    const token = getToken();

    const buildThumbUrl = (path) =>
      `${globalThis.REACT_APP_API_PURE}record/download/file/${encodeURIComponent(
        path.replaceAll('/', '%2F')
      )}/thumbnail`;

    const getExt = (name) => {
      const i = name.lastIndexOf('.');
      return i > -1 ? name.slice(i + 1).toLowerCase() : 'png';
    };

    const pushPreview = (setter, item) => setter((prev) => [...prev, item]);

    const fetchThumb = async (url) => {
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.statusText || 'Download failed');
      return res.blob();
    };

    // Generic handler for a group of files (before/after)
    // - `inferTypeFromName`: if true, preview type == file extension; else default to 'png'
    // - `loadingKey`: which loading flag to turn off when done ("loading1" / "loading2")
    async function processGroup(group, setPreviews, loadingKey, inferTypeFromName) {
      const entries = Object.entries(group || {});
      const tasks = entries.map(async ([filename, file]) => {
        const fileHash = file?.fileHash;
        const path = file?.metaData?.storageMetaData?.path;

        // No path provided → just echo it (keeps previous behavior)
        if (!path) {
          pushPreview(setPreviews, { url: path, type: 'png', path, filename, fileHash });
          return;
        }

        try {
          const blob = await fetchThumb(buildThumbUrl(path));
          const url = URL.createObjectURL(blob);
          const type = inferTypeFromName ? getExt(filename) : 'png';
          pushPreview(setPreviews, { url, type, path, filename, fileHash });
        } catch (err) {
          logger.error('Error fetching file:', err);
          // On error, still push a placeholder (previous behavior)
          pushPreview(setPreviews, { url: '', type: 'png', path, filename, fileHash });
        }
      });

      await Promise.all(tasks);
      setloading((s) => ({ ...s, [loadingKey]: false }));
    }

    // Run both in parallel
    await Promise.all([
      processGroup(before, setImagePreviews, 'loading1', /* inferTypeFromName */ false), // before: keep 'png'
      processGroup(after, setImagePreviews2, 'loading2', /* inferTypeFromName */ true), // after: use filename ext
    ]);
  };

  const fetchInfo33 = useCallback(
    async (id) => {
      try {
        return fetch(`${globalThis.REACT_APP_API_PURE}record/changes/${id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        })
          .then((res) => res.json())
          .then((d) => {
            if (d.code != 401) {
              const total = d?.unAuthorizedChanges;

              setDatachanges((currenData) => [
                ...currenData,
                {
                  id,
                  total,
                  authorizedChanges: d?.authorizedChanges,
                  unAuthorizedChanges: d?.unAuthorizedChanges,
                },
              ]);
            }
          });
      } catch (error) {
        logger.error('Error in fetchInfo4:', error);
      }
    },
    [datachanges, setDatachanges]
  );

  useEffect(() => {
    // setFiles
    const decodedId = atob(id);
    setDatadecoded(JSON.parse(decodedId));
    fetchInfo7(`${globalThis.REACT_APP_API_PURE}file/list?alert_id=${JSON.parse(decodedId)?.id}`, setFiles);
    fetchInfo5(`${globalThis.REACT_APP_API_PURE}alert/history?id=${JSON.parse(decodedId)?.id}`, setHistory);
    fetchInfo(`${globalThis.REACT_APP_API_PURE}alert/${JSON.parse(decodedId)?.id}/data`, decodedId);
  }, []);

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve({ name: file.name, size: file.size, contents: base64String, type: file.type });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileChange = (payload) => {
    const selectedFiles = payload?.target?.files
      ? Array.from(payload.target.files)
      : Array.isArray(payload)
      ? payload
      : [];
    if (!selectedFiles.length) {
      return;
    }

    const validFiles = [];
    const invalidMessages = [];

    selectedFiles.forEach((file) => {
      const extension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
      if (!ALLOWED_EXTENSIONS.includes(extension) || !ALLOWED_MIME_TYPES.has(file.type)) {
        invalidMessages.push(`${file.name} (${t('Uploadfiles.invalidType', { defaultValue: 'type not allowed' })})`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        invalidMessages.push(`${file.name} (${t('Uploadfiles.tooBig', { defaultValue: 'exceeds 5MB' })})`);
        return;
      }
      validFiles.push(file);
    });

    if (invalidMessages.length) {
      notification.error({
        message: t('Uploadfiles.invalidFiles', { defaultValue: 'Some files were rejected' }),
        description: invalidMessages.join(', '),
      });
    }

    if (!validFiles.length) {
      return;
    }

    Promise.all(validFiles.map(readFileAsBase64))
      .then((fileObjects) => setFiles((prevState) => [...prevState, ...fileObjects]))
      .catch((error) => logger.error('Error reading files:', error));
  };

  const handleRemoveFile = React.useCallback(
    (indexToRemove) => {
      setFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    },
    [setFiles]
  );

  return (
    <Container>
      <DialogUpload
        open={dialogUpload.open}
        handleClose={dialogUpload.handleClose}
        handleFileChange={handleFileChange}
        files={files}
        onRemoveFile={handleRemoveFile}
      />
      {dialogUploadPreview.open && (
        <DialogPreview
          open={dialogUploadPreview.open}
          handleClose={handleClosePreview}
          // handleFileChange={handleFileChange}
          //  data={dialogUploadPreview.base64String}
          // files={files}
          path={dialogUploadPreview.path}
          type={dialogUploadPreview.type}
          filename={dialogUploadPreview.filename}
        />
      )}
      <Grid container direction={'row'} spacing={2}>
        <Grid item xs={12}>
          {CardBack(navigate)}
        </Grid>
        <Grid item xs={12}>
          {CardHeader(datadecoded, datachanges, orderlist)}
        </Grid>
        <Grid item xs={12}>
          {CardHeaderHistory({ orderlist })}
        </Grid>
        <Grid item xs={12} container direction={'row'} spacing={2}>
          <Grid item xs={6}>
            <Card style={{ padding: 20 }}>
              <Grid container direction={'row'} justifyContent={'space-between'}>
                <Grid item container direction={'row'} spacing={1}>
                  <Grid item xs={12} container direction={'row'} justifyContent={'space-between'}>
                    <Grid item>
                      <Typography variant="h5">
                        {orderlist?.length == 1 ? t('HasnoAuthUpdate.1') : t('BeforeUpdate.1')}
                      </Typography>
                    </Grid>
                  </Grid>
                  {orderlist?.length == 1 ? (
                    ''
                  ) : (
                    <Grid item xs={12} container direction={'row'} spacing={2}>
                      <Grid item>
                        {formatEpochToDateString(orderlist[selectedIndex]?.epoch) == 'Invalid Date'
                          ? ''
                          : formatEpochToDateString(orderlist[selectedIndex]?.epoch)}
                      </Grid>
                      <Grid item>
                        <Typography>To</Typography>
                      </Grid>
                      <Grid item>
                        {' '}
                        {formatEpochToDateString(orderlist[selectedIndexafter]?.epoch) == 'Invalid Date'
                          ? ''
                          : formatEpochToDateString(orderlist[selectedIndexafter]?.epoch)}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Paper
              style={{
                padding: 20,
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
              }}
            >
              <Grid container direction={'row'} justifyContent={'space-between'}>
                <Grid item container direction={'row'} xs spacing={2}>
                  <Grid item>
                    {' '}
                    <Typography variant="h5">{t('DataUpdate.1')}</Typography>
                  </Grid>{' '}
                  <Grid item xs={7}>
                    <Autocomplete
                      disableClearable
                      fullWidth
                      size="small"
                      key={id}
                      disablePortal
                      id={id}
                      onChange={(event, newValue) => {
                        const selectedIndex = orderlist?.findIndex((option) => option.epoch === newValue.epoch);
                        let selectedIndexafter; // tanpa assign default

                        const hashAfter = [];

                        if (selectedIndex > 1) {
                          hashAfter.push(orderlist[selectedIndex]);

                          selectedIndexafter =
                            orderlist?.findIndex((option) => option.epoch === hashAfter[0].epoch) - 1;
                        } else {
                          hashAfter.push(orderlist[1]);
                          if (orderlist[1].unauthorized) {
                            selectedIndexafter = 0;
                          } else {
                            selectedIndexafter = 1;
                          }
                        }

                        setDate(newValue.epoch);
                        fetchInfo4(
                          `${globalThis.REACT_APP_API_PURE}record/compare/${datadecoded.recordId}/${hashAfter[0]?.id}`,
                          setData,
                          { isChange: true },
                          0,
                          selectedIndexafter < 1 ? 0 : selectedIndexafter
                        );
                      }}
                      options={orderlist.slice(1)}
                      getOptionLabel={(dropdown) => formatEpochToDateString(dropdown.timestamp) || ''}
                      isOptionEqualToValue={(option, selectedValue) => option.epoch === selectedValue.epoch}
                      renderInput={(params) => (
                        <TextField
                          placeholder={formatEpochToDateString(date)}
                          key={id}
                          {...params}
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: '12px' },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        <Grid item xs={12} container direction={'row'} spacing={2}>
          <Grid item xs={6}>
            {GererateDataBeforeAfter({
              title: t('RecordfromBlockchain.1'),
              data: data[0]?.history.height == 1 ? {} : data[0]?.history?.before,
              invalidpath: data[0]?.history?.invalids,
              type: 'add',
              previews: imagePreviews,
              fileName: image.nameFileBefore,
              setDialogUploadPreview,
              before: '',
              after: data[0]?.history?.after,
              loading: loading.loading1,
              updatepath: data[0]?.history?.updates,
              height: data[0]?.history.height,
              closedStatus: datadecoded.closedStatus,
              authorized: data[0]?.history?.authorized,
              unauthorized: data[0]?.history?.unauthorized,
            })}
          </Grid>
          <Grid item xs={6}>
            {GererateDataBeforeAfter({
              title: t('DataVerifResult.1'),
              data: data[0]?.history?.after,
              invalidpath: data[0]?.history?.invalids,
              type: 'remove',
              previews: imagePreviews2,
              fileName: image.nameFileAfter,
              setDialogUploadPreview,
              before: data[0]?.history?.before,
              after: '',
              loading: loading.loading2,
              updatepath: data[0]?.history?.updates,
              height: data[0]?.history.height,
              closedStatus: datadecoded.closedStatus,
            })}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {ActionSection({
            action: action?.data,
            dataset: datadecoded?.dataSet,
            setAction,
            status: datadecoded?.status,
            fetchInfo6,
            fetchapprovechecker,

            dialogUpload,
            files,
            setFiles,
            newstatusprocess,
            setNewstatusprocess,
            closedStatus: datadecoded.closedStatus,
            acl,
          })}
        </Grid>
        <Grid item xs={12}>
          {HisrotySection({ history: history?.data || [] })}
        </Grid>
      </Grid>
    </Container>
  );
}

function GererateDataBeforeAfter({
  title,
  data,
  invalidpath,
  type,
  previews,
  fileName,
  setDialogUploadPreview,
  before,
  after,
  loading,
  updatepath,
  height,
  closedStatus,
  authorized,
  unauthorized,
}) {
  return (
    <Card style={{ padding: 20 }}>
      <Grid container direction={'row'}>
        <Grid item xs={12} container direction={'row'} spacing={2}>
          <Grid item alignSelf={'center'}>
            {' '}
            <CircleIcon style={{ color: '#1EB3D9', fontSize: 10 }} />
          </Grid>
          <Grid item>
            <Typography variant="h5">{title}</Typography>
          </Grid>
          {GenerateObject({
            data,
            invalidpath,
            initialinvalidpath: '/',
            type,
            before,
            after,
            updatepath,
            height,
            closedStatus,
            authorized,
            unauthorized,
          })}
        </Grid>
        <Grid item container direction={'row'} xs={12} style={{ padding: 20 }}>
          {previews.map(
            (preview, index) =>
              !isValidFileName(preview.filename, preview?.fileHash) && (
                <Grid item xs={6} key={preview.fileHash || preview.filename}>
                  {type !== 'add' && (
                    <Typography style={{ color: getTypographyColor(type, preview, invalidpath, updatepath) }}>
                      {getStatusText(preview, invalidpath, updatepath)}
                    </Typography>
                  )}

                  <Card
                    raised
                    sx={{
                      backgroundColor: getCardBackground(
                        type,
                        preview,
                        invalidpath,
                        updatepath,
                        height,
                        authorized,
                        unauthorized
                      ),
                      maxWidth: 150,
                      margin: '0 auto',
                      padding: '0.1em',
                      position: 'relative',
                      transition: 'box-shadow 0.3s ease',
                      '&:hover': { boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={preview.url || (preview.path ? Nopreview : ImageNull)}
                      alt={`Preview ${index}`}
                      sx={{ padding: '1em 1em 0 1em', objectFit: 'contain' }}
                      onClick={() => {
                        if (preview.path && preview.fileHash) {
                          setDialogUploadPreview({
                            open: true,
                            base64String: preview.url,
                            type: preview.type,
                            path: preview.path,
                            filename: preview.filename,
                          });
                        }
                      }}
                    />
                    <CardContent style={{ textAlign: 'center' }}>
                      <Typography variant="caption">
                        {preview.path && preview.fileHash ? preview.filename : ''}
                      </Typography>
                    </CardContent>

                    {preview.filename.includes('pdf') && (
                      <PictureAsPdfIcon
                        fontSize="large"
                        sx={{ position: 'absolute', bottom: 0, right: 0, color: 'red' }}
                      />
                    )}
                    {preview.filename.includes('mp3') && (
                      <LibraryMusicIcon
                        fontSize="large"
                        sx={{ position: 'absolute', bottom: 0, right: 0, color: '#1EB3D9' }}
                      />
                    )}
                  </Card>
                </Grid>
              )
          )}
        </Grid>
      </Grid>
    </Card>
  );
}

GererateDataBeforeAfter.propTypes = {
  title: PropTypes.string,
  data: PropTypes.object,
  invalidpath: PropTypes.string,
  type: PropTypes.string,
  previews: PropTypes.array,
  fileName: PropTypes.string,
  setDialogUploadPreview: PropTypes.func,
  before: PropTypes.object,
  after: PropTypes.object,
  loading: PropTypes.bool,
  updatepath: PropTypes.string,
  height: PropTypes.number,
  closedStatus: PropTypes.bool,
  authorized: PropTypes.bool,
  unauthorized: PropTypes.bool,
};

const isValidFileName = (fileName, filehash) => {
  if (filehash != null || filehash != undefined) {
    return false;
  }

  const invalidExtensions = /\.(pdf|jpg|png|doc|docx|xls|xlsx)$/i;

  return !invalidExtensions.test(fileName);
};

function determineType(data) {
  if (Array.isArray(data)) {
    return 'array';
  }
  if (typeof data === 'object' && data !== null) {
    return 'object';
  }
  return 'unknown';
}
function GenerateObject({
  data,
  invalidpath,
  initialinvalidpath: newinitialinvalidpath,
  type,
  updatepath,
  height,
  authorized,
  unauthorized,
  closedStatus,
  before,
  after,
}) {
  return Object.keys(data || {}).map((key) => {
    const value = data[key];
    let title = capitalizeFirstLetter(key);

    // Replace specific titles
    if (title === 'FileLeafRef' || title === 'File Leaf Ref') {
      title = 'Filename';
    } else if (title === 'FileRef' || title === 'File Ref') {
      title = 'File Path';
    } else if (title === 'FileDirRef' || title === 'File Dir Ref') {
      title = 'Folder Directory';
    }

    const pathKey = newinitialinvalidpath + key;

    const invalidobject = getInvalidObject(invalidpath, pathKey);
    const validobject = getValidObject(updatepath, pathKey);

    // ---- case 1: skip irrelevant keys ----
    if (
      key.toLowerCase().includes('hash') ||
      ['bucketname', 'key', 'path'].includes(key) ||
      ['Bucket Name', 'Object Type', 'Location'].includes(title) ||
      value === 'ap-southeast-4'
    ) {
      return <span key={key}> </span>;
    }

    // ---- case 2: null values ----
    if (value == null) {
      return (
        <Grid item xs={12} container direction="row" justifyContent="space-between" key={key}>
          <Grid item>
            <Typography
              variant="body2"
              style={{
                color: getTypographyColorOld(
                  type,
                  pathKey,
                  invalidpath,
                  updatepath,
                  height,
                  authorized,
                  unauthorized,
                  validobject,
                  invalidobject
                ),
              }}
            >
              {title}
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body2"
              style={{
                color: getTypographyColorOld(
                  type,
                  pathKey,
                  invalidpath,
                  updatepath,
                  height,
                  authorized,
                  unauthorized,
                  validobject,
                  invalidobject
                ),
              }}
            >
              {getStatusTextOld(
                type,
                pathKey,
                invalidpath,
                updatepath,
                height,
                authorized,
                unauthorized,
                validobject,
                invalidobject
              )}
            </Typography>
          </Grid>
          <TextField
            variant="outlined"
            value=""
            fullWidth
            InputProps={{
              readOnly: true,
              style: getInputStyles(
                type,
                pathKey,
                invalidpath,
                updatepath,
                height,
                authorized,
                unauthorized,
                validobject,
                invalidobject
              ),
            }}
          />
        </Grid>
      );
    }

    // ---- case 3: object values (recurse) ----
    if (typeof value === 'object') {
      return (
        <Grid item container direction="row" spacing={2} key={key}>
          <Grid item>
            <Typography variant="h5">{title}</Typography>
          </Grid>
          {GenerateObject({
            data: value,
            invalidpath,
            initialinvalidpath: `${pathKey}/`,
            type,
            updatepath,
            height,
            authorized,
            unauthorized,
            closedStatus,
            before,
            after,
          })}
        </Grid>
      );
    }

    // ---- case 4: default scalar values ----
    return (
      <Grid item xs={12} container direction="row" justifyContent="space-between" key={key}>
        <Grid item>
          <Typography
            variant="body2"
            style={{
              color: getTypographyColorOld(
                type,
                pathKey,
                invalidpath,
                updatepath,
                height,
                authorized,
                unauthorized,
                validobject,
                invalidobject
              ),
            }}
          >
            {title}
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            variant="body2"
            style={{
              color: getTypographyColorOld(
                type,
                pathKey,
                invalidpath,
                updatepath,
                height,
                authorized,
                unauthorized,
                validobject,
                invalidobject
              ),
            }}
          >
            {getStatusTextOld(
              type,
              pathKey,
              invalidpath,
              updatepath,
              height,
              authorized,
              unauthorized,
              validobject,
              invalidobject
            )}
          </Typography>
        </Grid>
        <TextField
          variant="outlined"
          value={value}
          fullWidth
          InputProps={{
            readOnly: true,
            style: getInputStyles(
              type,
              pathKey,
              invalidpath,
              updatepath,
              height,
              authorized,
              unauthorized,
              validobject,
              invalidobject
            ),
          }}
        />
      </Grid>
    );
  });
}

GenerateObject.propTypes = {
  data: PropTypes.object,
  invalidpath: PropTypes.string,
  initialinvalidpath: PropTypes.string,
  type: PropTypes.string,
  updatepath: PropTypes.string,
  height: PropTypes.number,
  authorized: PropTypes.bool,
  unauthorized: PropTypes.bool,
  closedStatus: PropTypes.bool,
  before: PropTypes.object,
  after: PropTypes.object,
};

const capitalizeFirstLetter = (str) => formatCamelCase(str.charAt(0).toUpperCase() + str.slice(1));
const checkValueType = (value) => typeof value;

function ComponentAuth({ color, title }) {
  return (
    <Grid item container xs={2.5} direction={'row'}>
      <Grid item container direction={'row'} spacing={2} style={{ paddingLeft: 30 }}>
        <Grid item alignSelf={'center'}>
          <CircleIcon style={{ color, fontSize: 15 }} />
        </Grid>
        <Grid item>
          <Typography variant="caption" style={{ color: '#888888' }}>
            {' '}
            {title}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}

ComponentAuth.propTypes = {
  color: PropTypes.string,
  title: PropTypes.string,
};

function CardHeader(data, datachanges, orderlist) {
  const { t } = useTranslation();

  return (
    <Card style={{ padding: 20 }}>
      <Grid container direction={'row'} spacing={2}>
        <Grid item xs={12} container direction={'row'} spacing={2}>
          <Grid item alignSelf={'center'}>
            {' '}
            <CircleIcon style={{ color: '#1EB3D9', fontSize: 10 }} />
          </Grid>
          <Grid item>
            <Typography variant="h5">Alert Id : {data.id}</Typography>
          </Grid>
        </Grid>
        {Detail({
          title: t('DateTime.1'),
          value: data.createdAt,
          title2: 'Dataset',
          value2: data?.dataSet ?? '',
        })}
        {Detail({
          title: 'Record ID',
          value: data?.recordId,
          title2: 'Folder',
          value2: data?.folder ?? '',
        })}
        {Detail({
          title: t('FileName.1'),
          value: data?.recordName || '',
          title2: t('NumberOf.1'),
          value2: data?.number || 0,
        })}
      </Grid>
    </Card>
  );
}
const isAnyNoteEmpty = (data, status) => {
  for (const entry of data) {
    for (const step of entry.steps) {
      if (!step.note.trim()) {
        return true;
      }
    }
  }
  return false;
};

function HisrotySection({ history = [] }) {
  return (
    <Card style={{ padding: 20 }}>
      {' '}
      <Grid container direction={'row'} spacing={2}>
        <Grid item xs={12} container direction={'row'} spacing={2}>
          <Grid item alignSelf={'center'}>
            {' '}
            <CircleIcon style={{ color: '#1EB3D9', fontSize: 10 }} />
          </Grid>
          <Grid item>
            <Typography variant="h5">History</Typography>
          </Grid>
        </Grid>

        {history.map((item, index) => {
          const formattedCreatedAt = formatToJakartaDateTime(item.created_at);
          return (
            <Grid
              key={`${item.created_at}-${item.description}-${index}`}
              item
              xs={12}
              container
              direction={'row'}
              spacing={2}
              style={{ paddingLeft: 40 }}
            >
              <Grid item alignSelf={'center'}>
                <CircleIcon style={{ color: '#BFEAF8', fontSize: 10 }} />
              </Grid>
              <Grid item>
                <Avatar sizes="small" />
              </Grid>
              <Grid item container direction={'row'} xs={3}>
                <Grid item xs={12}>
                  <Typography variant="caption">{item.description}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption">{formattedCreatedAt}</Typography>
                </Grid>
              </Grid>
              <Grid item xs={1} style={{ borderRight: '1px solid #e0e0e0', margin: 10 }}>
                {/* Empty content for the vertical line, adjust padding as needed */}
              </Grid>
              <Grid item xs={5}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '4px',
                  }}
                >
                  <Typography variant="subtittle2" style={{ textAlign: 'right' }}>
                    {(() => {
                      // Ensure status exists and is a string
                      if (!item.status) {
                        return item.description || '';
                      }

                      // Handle both inProgress (mixed case) and inprogress (lowercase)
                      const statusStr = String(item.status);
                      const normalizedStatus = statusStr.toLowerCase();

                      if (statusStr === 'inProgress' || normalizedStatus === 'inprogress') {
                        return `${item.Role || ''} Change The Status Into In Progress`;
                      }

                      const statusText = getHistoryStatusText(statusStr);
                      return `${item.Role || ''} Change The Status Into ${statusText}`;
                    })()}
                  </Typography>
                  {item.note &&
                    item.note.trim() !== '' &&
                    item.note.trim().toLowerCase() !== 'keep' &&
                    item.note.trim().toLowerCase() !== 'rollback' && (
                      <Typography
                        variant="body2"
                        style={{
                          color: '#666',
                          fontSize: '0.875rem',
                          textAlign: 'right',
                        }}
                      >
                        note : {item.note}
                      </Typography>
                    )}
                </div>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Card>
  );
}

HisrotySection.propTypes = {
  history: PropTypes.array,
};

function ActionSection({
  action = [],
  setAction,
  status,
  fetchInfo6,
  fetchapprovechecker,
  dialogUpload,
  files,
  setFiles,
  newstatusprocess,
  setNewstatusprocess,
  closedStatus,
  dataset,
  acl,
}) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1000,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    boxShadow: 5,
    p: 4,
  };

  const [openeds, setOpeneds] = React.useState(false);
  const [openedsroll, setOpenedsroll] = React.useState(false);

  const [Rejects, setRejects] = React.useState(false);

  const handleOpeneds = () => setOpeneds(true);
  const handleOpenedsroll = () => setOpenedsroll(true);

  const handleRejects = () => setRejects(true);

  const handleCloseds = () => {
    setOpeneds(false);
    setNewstatusprocess((prev) => ({ ...prev, note: '' }));
  };
  const handleClosedsroll = () => {
    setOpenedsroll(false);
    setNewstatusprocess((prev) => ({ ...prev, note: '' }));
  };

  const handleClosedsRejects = () => {
    setRejects(false);
    setNewstatusprocess((prev) => ({ ...prev, note: '' }));
  };

  const cancelButtonRef = useRef(null);

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [downloadingIds, setDownloadingIds] = useState(new Set());

  const formatFileSize = (bytes) => {
    if (!bytes || Number.isNaN(bytes)) {
      return '';
    }
    const mb = bytes / (1024 * 1024);
    return `${mb >= 0.01 ? mb.toFixed(2) : '0.01'} MB`;
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== idx));
  };

  const renderUploadedFiles = ({ allowDownload = false, allowRemove = false, metaFormatter } = {}) => {
    if (!Array.isArray(files) || !files.length) {
      return null;
    }
    return (
      <Grid item xs={12} className="flex justify-center">
        <Stack spacing={1.5} className="w-full max-w-2xl">
          {files.map((item, index) => {
            const name =
              item.nama_file ||
              item.name ||
              item.filename ||
              `${t('Uploadfiles.file', { defaultValue: 'File' })} ${index + 1}`;
            const sizeText = typeof item.size === 'number' && item.size > 0 ? formatFileSize(item.size) : undefined;
            const meta = metaFormatter ? metaFormatter(item) : undefined;
            const infoText = [sizeText, meta].filter(Boolean).join(' · ');
            const canDownload = allowDownload && Boolean(item.id);
            const isDownloading = canDownload && downloadingIds.has(item.id);
            return (
              <Paper
                key={`uploaded-${item.id || name}-${index}`}
                className="flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 shadow-none"
              >
                <Box className="flex items-center justify-center rounded-full bg-white p-1">
                  <CloudCircleIcon className="text-slate-400" fontSize="small" />
                </Box>
                <Box className="flex-1 min-w-0 text-left">
                  <Typography variant="body2" className="truncate font-semibold text-slate-800">
                    {name}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500">
                    {[infoText, t('Uploadfiles.5')].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {canDownload && (
                  <IconButton
                    aria-label={t('DownloadButton', { defaultValue: 'Download file' })}
                    color="success"
                    size="small"
                    disabled={isDownloading}
                    onClick={() => handleDownload(item.id, item.nama_file || item.name)}
                  >
                    <ArrowCircleDownOutlined fontSize="small" />
                  </IconButton>
                )}
                {allowRemove && (
                  <IconButton
                    aria-label={t('RemoveButton', { defaultValue: 'Remove file' })}
                    color="error"
                    size="small"
                    onClick={() => removeFileAt(index)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Paper>
            );
          })}
        </Stack>
      </Grid>
    );
  };

  const handleDownload = async (id, fileName) => {
    if (downloadingIds.has(id)) return; // Prevent double-click
    setDownloadingIds((prev) => new Set(prev).add(id));
    try {
      const response = await fetch(`${globalThis.REACT_APP_API_PURE}file/download?id=${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Error downloading the file:', error);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleClickProceed = async () => {
    if (isLoading) return; // Prevent double-click
    setIsLoading(true);
    try {
      if (isAnyNoteEmpty(action, status)) {
        if (status == 'inProgress') {
          await fetchInfo6(newstatusprocess.status, newstatusprocess.closed_status);
        } else {
          await fetchInfo6('inProgress', '');
        }
      } else if (status == 'open') {
        await fetchInfo6(newstatusprocess.status, newstatusprocess.closed_status);
      } else {
        await fetchInfo6(newstatusprocess.status, newstatusprocess.closed_status);
      }
    } catch (error) {
      logger.error('Error in handleClickProceed:', error);
      setIsLoading(false);
    }
    // Note: setIsLoading(false) not needed here as navigate will unmount component
  };
  const handleClickApprove = async () => {
    if (isLoading) return; // Prevent double-click
    setIsLoading(true);
    try {
      await fetchapprovechecker(newstatusprocess.status, newstatusprocess.closed_status, newstatusprocess.note);
    } catch (error) {
      logger.error('Error in handleClickApprove:', error);
      setIsLoading(false);
    }
    // Note: setIsLoading(false) not needed here as navigate will unmount component
  };

  const handleClickReject = async () => {
    if (isLoading) return; // Prevent double-click
    setIsLoading(true);
    try {
      await fetchapprovechecker(newstatusprocess.status, newstatusprocess.closed_status, newstatusprocess.note);
    } catch (error) {
      logger.error('Error in handleClickReject:', error);
      setIsLoading(false);
    }
    // Note: setIsLoading(false) not needed here as navigate will unmount component
  };

  const translatedData = closedStatus ? t(`closedtatus1.${closedStatus}`) : null;
  return (
    <Card style={{ padding: 20 }}>
      <Grid container direction={'row'} spacing={2}>
        <Grid item xs={12} container direction={'row'} spacing={2}>
          <Grid item alignSelf={'center'}>
            {' '}
            <CircleIcon style={{ color: '#1EB3D9', fontSize: 10 }} />
          </Grid>
          <Modal
            open={openeds}
            onClose={handleCloseds}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-7 w-7 text-yellow-500" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Typography as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {t('ProceedButton.1')}
                    </Typography>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{t('ProcessPrompt.1')}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  aria-label={isLoading ? 'Processing...' : t('ProceedButton.1') || 'Proceed'}
                  disabled={isLoading}
                  style={{ backgroundColor: isLoading ? 'gray' : 'var(--yellow-400, #FFD700)', color: 'white' }}
                  className="inline-flex w-full justify-center rounded-md bg-yellow-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 sm:ml-3 sm:w-auto"
                  onClick={() => {
                    handleClickProceed();
                  }}
                >
                  {isLoading ? 'Loading...' : t('ProceedButton.1')}
                </button>
                <button
                  type="button"
                  aria-label={t('CancelButton.1') || 'Cancel'}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={() => setOpeneds(false)}
                  ref={cancelButtonRef}
                >
                  {t('CancelButton.1')}
                </button>
              </div>
            </Box>
          </Modal>
          <Modal
            open={openedsroll}
            onClose={handleClosedsroll}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-7 w-7 text-yellow-500" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Typography as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {t('PromptApproval.1', { data: translatedData })}
                    </Typography>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t('PromptApproval.2')} {t(`closedtatus2.${closedStatus}`)}
                        {t('PromptApproval.3')}
                      </p>
                    </div>
                    <div className="mt-4">
                      <Typography variant="body2" className="text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-600 underline">
                          {t('AddComment.1', { defaultValue: 'Tambahkan kolom komentar, pada aksi ini' })}
                        </span>
                        <span className="text-gray-500 text-xs ml-2">
                          ({t('CommentOptional.1', { defaultValue: 'Opsional' })})
                        </span>
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={t('CommentPlaceholder.1', { defaultValue: 'Masukkan komentar...' })}
                        value={newstatusprocess.note || ''}
                        onChange={(e) => {
                          setNewstatusprocess((prev) => ({ ...prev, note: e.target.value }));
                        }}
                        variant="outlined"
                        size="small"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  disabled={isLoading}
                  style={{ backgroundColor: isLoading ? 'gray' : 'var(--yellow-400, #FFD700)', color: 'white' }}
                  className="inline-flex w-full justify-center rounded-md bg-yellow-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 sm:ml-3 sm:w-auto"
                  onClick={() => {
                    handleClickApprove();
                  }}
                >
                  {isLoading ? 'Loading...' : t(`closedtatus1.${closedStatus}`)}
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={() => setOpenedsroll(false)}
                  ref={cancelButtonRef}
                >
                  {t('Cancel.1')}
                </button>
              </div>
            </Box>
          </Modal>
          <Modal
            open={Rejects}
            onClose={handleClosedsRejects}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-7 w-7 text-yellow-500" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Typography as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {t('PromptReject.1')}
                    </Typography>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{t('PromptReject.2')}</p>
                    </div>
                    <div className="mt-4">
                      <Typography variant="body2" className="text-sm font-medium text-gray-700 mb-2">
                        <span className="text-red-600 underline">
                          {t('AddComment.1', { defaultValue: 'Tambahkan kolom komentar, pada aksi ini' })}
                        </span>
                        <span className="text-gray-500 text-xs ml-2">
                          ({t('CommentOptional.1', { defaultValue: 'Opsional' })})
                        </span>
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={t('CommentPlaceholder.1', { defaultValue: 'Masukkan komentar...' })}
                        value={newstatusprocess.note || ''}
                        onChange={(e) => {
                          setNewstatusprocess((prev) => ({ ...prev, note: e.target.value }));
                        }}
                        variant="outlined"
                        size="small"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  disabled={isLoading}
                  style={{ backgroundColor: isLoading ? 'gray' : 'var(--yellow-400, #FFD700)', color: 'white' }}
                  className="inline-flex w-full justify-center rounded-md bg-yellow-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 sm:ml-3 sm:w-auto"
                  onClick={() => {
                    handleClickReject();
                  }}
                >
                  {isLoading ? 'Loading...' : t('Reject.1')}
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={() => setRejects(false)}
                  ref={cancelButtonRef}
                >
                  {t('Cancel.1')}
                </button>
              </div>
            </Box>
          </Modal>
          <Grid item>
            <Typography variant="h5">Action</Typography>
          </Grid>
        </Grid>

        {action == null && acl?.create && privilage(status) ? (
          <Grid item xs={12} container direction={'row'} justifyContent={'center'}>
            <Link to={`/dashboard/action/add?dataset=${dataset}`}>
              <Button variant="outlined" style={{ color: '#30a2ce' }}>
                {t('AssignAction.1')}
              </Button>
            </Link>
          </Grid>
        ) : (
          action?.map((item, index) => (
            <Grid key={`action-${item.action_name}-${index}`} item xs={12} container direction={'row'}>
              {' '}
              <Grid item>
                <Typography style={{ marginLeft: 25 }}>{item.action_name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <hr style={{ marginTop: 20 }} />
              </Grid>
              {item.steps.map((item2, index) => (
                <Grid key={`step-${item2.step_name || item2.id || index}`} item xs={12} container direction={'row'}>
                  <Grid item style={{ marginBottom: 30 }} xs={12}>
                    {' '}
                  </Grid>
                  <Grid item xs={12} container direction={'row'} justifyContent={'space-between'}>
                    <Grid item alignSelf={'center'} xs={6}>
                      <Typography>{item2.description}</Typography>
                    </Grid>
                    <Grid item md={6}>
                      <TextField
                        multiline
                        disabled={
                          status == 'pendingApproval' ||
                          status == 'pendingRollback' ||
                          status == 'closed' ||
                          !privilage(status)
                        }
                        fullWidth
                        variant="outlined"
                        onChange={(event) => {
                          setAction((prevState) => ({
                            ...prevState,
                            data: prevState.data.map((item3) => {
                              if (item3.id === item.id) {
                                item3.steps.map((item4) => {
                                  if (item4.id === item2.id) {
                                    item4.note = event.target.value;
                                  }
                                });
                              }
                              return item3;
                            }),
                          }));
                        }}
                        value={item2.note}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
              <Grid item xs={12}>
                <hr style={{ marginTop: 20 }} />
              </Grid>
              {(() => {
                const isReadOnlyView = status === 'closed' || !privilage(status);
                if (!isReadOnlyView || index + 1 !== action.length) {
                  return null;
                }
                return renderUploadedFiles({
                  allowDownload: true,
                  metaFormatter: (file) => formatToJakartaDate(file.created_at),
                });
              })()}
              {status === 'open' && privilage(status) && (
                <Grid
                  item
                  xs={12}
                  container
                  direction={'row'}
                  justifyContent={'center'}
                  spacing={2}
                  style={{ marginTop: 10 }}
                >
                  {index + 1 === action.length && renderUploadedFiles({ allowRemove: true })}
                  {index + 1 === action.length && (
                    <Grid item xs={12} style={{ textAlignLast: 'center' }}>
                      <Button
                        onClick={() => {
                          dialogUpload.handleClose();
                        }}
                      >
                        {' '}
                        {t('UploadButton.1')}
                      </Button>
                    </Grid>
                  )}
                  {index + 1 === action.length && isAnyNoteEmpty(action, status) ? (
                    <Grid item container direction={'row'} spacing={2} xs={12} justifyContent={'center'}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewstatusprocess({
                              status: 'open',
                              closed_status: '',
                            });
                            handleOpeneds();
                          }}
                        >
                          {t('SaveButton.2')}
                        </Button>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid item container direction={'row'} spacing={2} xs={12} justifyContent={'center'}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewstatusprocess({
                              status: 'pendingApproval',
                              closed_status: 'rollback',
                            });
                            handleOpeneds();
                          }}
                          disabled={isAnyNoteEmpty(action, status)}
                        >
                          {t('RecommendationButton.1')}
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewstatusprocess({
                              status: 'pendingApproval',
                              closed_status: 'keep',
                            });
                            handleOpeneds();
                          }}
                          disabled={isAnyNoteEmpty(action, status)}
                        >
                          {t('RecommendationButton.2')}
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
              {status === 'inProgress' && privilage(status) && (
                <Grid
                  item
                  xs={12}
                  container
                  direction={'row'}
                  justifyContent={'center'}
                  spacing={2}
                  style={{ marginTop: 10 }}
                >
                  {index + 1 === action.length &&
                    renderUploadedFiles({
                      allowDownload: true,
                      allowRemove: true,
                    })}
                  {index + 1 === action.length && (
                    <Grid item xs={12} style={{ textAlignLast: 'center' }}>
                      <Button
                        onClick={() => {
                          dialogUpload.handleClose();
                        }}
                      >
                        {' '}
                        {t('UploadButton.1')}
                      </Button>
                    </Grid>
                  )}
                  {index + 1 === action.length && isAnyNoteEmpty(action, status) ? (
                    <Grid item container direction={'row'} spacing={2} xs={12} justifyContent={'center'}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewstatusprocess({
                              status: 'inProgress',
                              closed_status: '',
                            });
                            handleOpeneds();
                          }}
                        >
                          {t('SaveButton.1')}
                        </Button>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid item container direction={'row'} spacing={2} xs={12} justifyContent={'center'}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewstatusprocess({
                              status: 'pendingApproval',
                              closed_status: 'rollback',
                            });
                            handleOpeneds();
                          }}
                          disabled={isAnyNoteEmpty(action, status)}
                        >
                          {t('RecommendationButton.1')}
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewstatusprocess({
                              status: 'pendingApproval',
                              closed_status: 'keep',
                            });
                            handleOpeneds();
                          }}
                          disabled={isAnyNoteEmpty(action, status)}
                        >
                          {t('RecommendationButton.2')}
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
              {status === 'pendingApproval' && privilage(status) && (
                <Grid
                  item
                  xs={12}
                  container
                  direction={'row'}
                  justifyContent={'center'}
                  spacing={2}
                  style={{ marginTop: 0 }}
                >
                  {index + 1 === action.length &&
                    renderUploadedFiles({
                      allowDownload: true,
                      metaFormatter: (file) => formatToJakartaDate(file.created_at),
                    })}
                  {index + 1 === action.length && (
                    <Grid item>
                      <Button
                        disabled={isAnyNoteEmpty(action, status)}
                        variant="outlined"
                        onClick={() => {
                          setNewstatusprocess({
                            status: 'inProgress',
                            closed_status: 'Reject',
                          });

                          handleRejects();
                        }}
                        style={{
                          backgroundColor: 'white',
                          color: isAnyNoteEmpty(action, status) ? '#86DCF3' : '#12A2CE',
                        }}
                      >
                        {t('Reject.1')}
                      </Button>
                    </Grid>
                  )}
                  {index + 1 === action.length && (
                    <Grid item>
                      <Button
                        disabled={isAnyNoteEmpty(action, status)}
                        variant="outlined"
                        style={{
                          backgroundColor: isAnyNoteEmpty(action, status) ? '#86DCF3' : '#12A2CE',
                          color: 'white',
                        }}
                        onClick={() => {
                          if (closedStatus == 'keep') {
                            setNewstatusprocess({
                              status: 'closed',
                              closed_status: 'keep',
                            });
                          } else if (closedStatus == 'rollback') {
                            setNewstatusprocess({
                              status: 'pendingRollback',
                              closed_status: 'rollback',
                            });
                          }

                          handleOpenedsroll();
                        }}
                      >
                        {t('ApproveButton.1')} {t(`closedtatus.${closedStatus}`)}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
              {status === 'pendingRollback' && privilage(status) && (
                <Grid
                  item
                  xs={12}
                  container
                  direction={'row'}
                  justifyContent={'center'}
                  spacing={2}
                  style={{ marginTop: 10 }}
                >
                  {' '}
                  {index + 1 === action.length &&
                    renderUploadedFiles({
                      allowDownload: true,
                      metaFormatter: (file) => formatToJakartaDate(file.created_at),
                    })}
                  {index + 1 === action.length && <span />}
                </Grid>
              )}
              {status !== 'closed' &&
                !privilage(status) &&
                status !== 'open' &&
                status !== 'inProgress' &&
                status !== 'pendingApproval' &&
                status !== 'pendingRollback' && (
                  <Grid
                    item
                    xs={12}
                    container
                    direction={'row'}
                    justifyContent={'center'}
                    spacing={2}
                    style={{ marginTop: 10 }}
                  >
                    {index + 1 === action.length &&
                      renderUploadedFiles({
                        allowDownload: true,
                      })}
                    <Grid item>
                      <Button
                        disabled={isAnyNoteEmpty(action, status)}
                        variant="outlined"
                        onClick={handleOpenedskeep2}
                        style={{
                          backgroundColor: 'white',
                          color: isAnyNoteEmpty(action, status) ? '#86DCF3' : '#12A2CE',
                        }}
                      >
                        {t('KeepButton.1')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        disabled={isAnyNoteEmpty(action, status)}
                        variant="outlined"
                        style={{
                          backgroundColor: isAnyNoteEmpty(action, status) ? '#86DCF3' : '#12A2CE',
                          color: 'white',
                        }}
                        onClick={() => {
                          // Action button clicked
                        }}
                      >
                        Rollback
                      </Button>
                    </Grid>
                  </Grid>
                )}
            </Grid>
          ))
        )}
      </Grid>
    </Card>
  );
}

ActionSection.propTypes = {
  action: PropTypes.array,
  setAction: PropTypes.func,
  status: PropTypes.string,
  fetchInfo6: PropTypes.func,
  fetchapprovechecker: PropTypes.func,
  dialogUpload: PropTypes.object,
  files: PropTypes.array,
  setFiles: PropTypes.func,
  newstatusprocess: PropTypes.string,
  setNewstatusprocess: PropTypes.func,
  closedStatus: PropTypes.bool,
  dataset: PropTypes.object,
  acl: PropTypes.object,
};

function CardBack(navigate) {
  return (
    <Card style={{ padding: 20 }}>
      <Grid container direction={'row'}>
        <Grid item>
          <IconButton
            onClick={() => {
              navigate(-1);
            }}
            style={{ color: '#1EB3D9' }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item alignSelf={'center'}>
          <Typography variant="h5"> Details</Typography>
        </Grid>
      </Grid>
    </Card>
  );
}

CardBack.propTypes = {
  // PropTypes removed as they are not used
};

function Detail({ title, value, title2, value2 }) {
  return (
    <Grid item xs={12} container direction={'row'} style={{ marginLeft: 30 }}>
      <Grid item xs={2}>
        <Typography variant="body2">{title}</Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="body2">: {value}</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body2">{title2}</Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="body2"> {value2 == '' ? '' : `: ${value2}`}</Typography>
      </Grid>
    </Grid>
  );
}

Detail.propTypes = {
  title: PropTypes.string,
  value: PropTypes.string,
  title2: PropTypes.string,
  value2: PropTypes.string,
};

const formatCamelCase = (str) => {
  // Use a regular expression to split camelCase into words
  const words = str.split(/(?=[A-Z])/);

  // Join the words with a space
  const formattedString = words.join(' ');

  return formattedString;
};
function CardHeaderHistory({ orderlist }) {
  const { t } = useTranslation();
  return (
    <Card style={{ padding: 20 }}>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={12} container direction="row" spacing={2}>
          <Grid item alignSelf="center">
            <CircleIcon style={{ color: '#1EB3D9', fontSize: 10 }} />
          </Grid>
          <Grid item>
            <Typography variant="h5">{t('Historical.1')}</Typography>
          </Grid>
        </Grid>

        {ComponentAuth({ color: '#EE5020', title: t('UnauthorizedChanges.1') })}
        {ComponentAuth({ color: '#1EB3D9', title: t('AuthorizedChanges.1') })}

        <Grid item container direction="row" xs={12}>
          <div style={{ overflow: 'auto', marginTop: 10, marginBottom: 10, paddingLeft: 20 }}>
            <Grid item xs={12} container direction="row" style={{ overflowX: 'auto', minWidth: 'max-content' }}>
              {orderlist?.map((data, row) => (
                <div key={data.id || row}>
                  {MyComponent({
                    valid: data.authorized,
                    invalid: data.unauthorized,
                    timeEpoch: data.epoch,
                    isEndList: orderlist.length === row + 1,
                  })}
                </div>
              ))}
            </Grid>
          </div>
        </Grid>
      </Grid>
    </Card>
  );
}

CardHeaderHistory.propTypes = {
  orderlist: PropTypes.array,
};

function MyComponent({ valid = false, invalid = false, timeEpoch, isEndList = false }) {
  const date = new Date(timeEpoch);
  const options = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Jakarta' };
  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

  const hours = date.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  const formattedTime = `${formattedHours}:${minutes} ${ampm}`;
  return (
    <>
      <div className="div">
        <div className="div-2">
          <div className={'div-3'}>
            <div className={'div-4'}>
              {valid && invalid ? (
                <span>
                  <img loading="lazy" src={'/assets/X.png'} alt="line Logo" className="img" width="24" height="24" />

                  <img
                    loading="lazy"
                    src={'/assets/close.png'}
                    alt="line Logo"
                    className="img"
                    width="24"
                    height="24"
                  />
                </span>
              ) : (
                <img
                  loading="lazy"
                  src={valid ? '/assets/X.png' : '/assets/close.png'}
                  alt="line Logo"
                  className="img"
                  width="24"
                  height="24"
                />
              )}
            </div>
            {isEndList ? '' : <div className="div-5" />}
          </div>
          <div className="div-6">
            <img src="/assets/Line.png" alt="line Logo" className="w-100" width="100%" height="2" />
            <div className="div-7">
              <div className="div-8">{formattedDate}</div>
              <div className="div-9">{formattedTime}</div>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .div {
          display: flex;
          gap: 4px;
        }
        .div-2 {
          display: flex;
          flex-grow: 1;
          flex-basis: 0%;
          flex-direction: column;
        }
        .div-3 {
          display: flex;
          justify-content: space-between;
          gap: 4px;
        }
        .div-4 {
          justify-content: center;
          align-items: center;
          border-radius: 131.413px;
          border: 1.027px solid var(--Stroke-Light-Base, #e5e5e7);
          box-shadow: 0px 1.027px 2.053px 0px rgba(16, 24, 40, 0.04);
          background-color: var(--Background-Light-Base-Main, #fff);
          display: flex;
          aspect-ratio: 1;
          flex-direction: column;
          width: 44px;
          height: 44px;
          padding: 0 12px;
        }
        .img {
          aspect-ratio: 0.95;
          object-fit: contain;
          object-position: center;
          width: 100%;
          overflow: hidden;
        }
        .div-5 {
          background-color: #bfeaf8;
          align-self: center;
          width: 55px;
          height: 1px;
          margin: auto 0;
        }
        .div-6 {
          align-items: start;
          display: flex;
          margin-top: 4px;
          justify-content: space-between;
          gap: 8px;
          padding: 0 19px;
        }
        .img-2 {
          aspect-ratio: 0.07;
          object-fit: contain;
          object-position: center;
          width: 1px;
          stroke-width: 1px;
          stroke: var(--Blue-300, #86dcf3);
          overflow: hidden;
          max-width: 100%;
        }
        .div-7 {
          align-self: stretch;
          display: flex;
          flex-grow: 1;
          flex-basis: 0%;
          flex-direction: column;
        }
        .div-8 {
          color: var(--Blue-700, #0e7496);
          white-space: nowrap;
          font: 500 11px/127% Roboto, -apple-system, Roboto, Helvetica, sans-serif;
        }
        .div-9 {
          color: var(--Blue-700, #0e7496);
          white-space: nowrap;
          font: 500 11px/127% Roboto, -apple-system, Roboto, Helvetica, sans-serif;
        }
        .div-10 {
          display: flex;
          flex-grow: 1;
          flex-basis: 0%;
          flex-direction: column;
        }
        .div-11 {
          display: flex;
          justify-content: space-between;
          gap: 4px;
        }
        .div-12 {
          justify-content: center;
          align-items: center;
          border-radius: 131.413px;
          border: 1.027px solid var(--Stroke-Light-Base, #e5e5e7);
          box-shadow: 0px 1.027px 2.053px 0px rgba(16, 24, 40, 0.04);
          background-color: var(--Background-Light-Base-Main, #fff);
          display: flex;
          aspect-ratio: 1;
          flex-direction: column;
          width: 44px;
          height: 44px;
          padding: 0 12px;
        }
        .div-13 {
          background-color: #bfeaf8;
          align-self: center;
          width: 55px;
          height: 1px;
          margin: auto 0;
        }
        .div-14 {
          align-items: start;
          display: flex;
          margin-top: 4px;
          justify-content: space-between;
          gap: 8px;
          padding: 0 19px;
        }
        .img-3 {
          aspect-ratio: 0.07;
          object-fit: contain;
          object-position: center;
          width: 1px;
          stroke-width: 1px;
          stroke: var(--Red-400, #ff5b6c);
          overflow: hidden;
          max-width: 100%;
        }
        .div-15 {
          align-self: stretch;
          display: flex;
          flex-grow: 1;
          flex-basis: 0%;
          flex-direction: column;
        }
        .div-16 {
          color: var(--Red-600, #f90921);
          white-space: nowrap;
          font: 500 11px/127% Roboto, -apple-system, Roboto, Helvetica, sans-serif;
        }
        .div-17 {
          color: var(--Red-600, #f90921);
          white-space: nowrap;
          font: 500 11px/127% Roboto, -apple-system, Roboto, Helvetica, sans-serif;
        }
      `}</style>
    </>
  );
}

MyComponent.propTypes = {
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  timeEpoch: PropTypes.number,
  isEndList: PropTypes.bool,
};

const privilage = (status) => {
  const role = getRole();

  // SuperAdmin can only view, cannot perform any actions
  if (role === 'super_admin' || role === 'SuperAdmin' || role === 'SUPER_ADMIN') {
    return false;
  }

  // Based on the privilege table:
  // - Maker: Can process Open and InProgress alerts (Granted)
  // - Maker: Cannot process PendingApproval alerts (View Only)
  // - Checker: Cannot process Open and InProgress alerts (View Only)
  // - Checker: Can process PendingApproval alerts (Granted)

  // Maker cannot process pendingApproval alerts
  if (status === 'pendingApproval' && (role === 'maker' || role === 'Maker' || role === 'MAKER')) {
    return false;
  }

  // Checker cannot process open or inProgress alerts
  if (
    (status === 'open' || status === 'inProgress') &&
    (role === 'checker' || role === 'Checker' || role === 'CHECKER')
  ) {
    return false;
  }

  return true;
};

function transformChangesRemove(changes, before) {
  changes.forEach((change) => {
    const { path, operation, oldValue, newValue } = change;

    if (operation === 'remove' && (Array.isArray(oldValue) || typeof oldValue === 'object')) {
      const pathParts = path.split('/');
      let current = before;

      for (let i = 1; i < pathParts.length; i++) {
        const key = pathParts[i];
        if (i === pathParts.length - 1) {
          if (oldValue === null) {
            current[key] = null;
          } else if (Array.isArray(oldValue)) {
            current[key] = oldValue.map((obj) => Object.fromEntries(Object.keys(obj).map((k) => [k, null])));
          } else if (typeof oldValue === 'object') {
            current[key] = makeObjectValuesEmpty(oldValue);
          } else {
            current[key] = Object.fromEntries(Object.keys(oldValue).map((k) => [k, null]));
          }
        } else {
          if (typeof current[key] !== 'object' || current[key] === null) {
            current[key] = {};
          }
          current = current[key];
        }
      }
    }
  });

  return before;
}
function makeObjectValuesEmpty(obj) {
  const result = {};
  for (const key in obj) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      result[key] = makeObjectValuesEmpty(obj[key]);
    } else {
      result[key] = '';
    }
  }

  return result;
}
